import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import {
  GET_NOTES_KEY,
  NOTES_UPDATING_NOTES_ERROR_MESSAGE,
  SAVE_NOTES_EMPTY_ERROR,
} from 'common/constants/services/notes';
import { getPersistableNotes } from 'services/hooks/notes/utilities';
import NotesService from 'services/notes';
import { throwError } from 'utilities';
import { NotesFinalResponse, SaveNotesParams } from './types';

const saveNotes = async (params: SaveNotesParams): Promise<NotesFinalResponse> => {
  const { notes } = params;
  const persistableNotes = getPersistableNotes(notes);

  if (isEmpty(persistableNotes)) {
    return throwError(SAVE_NOTES_EMPTY_ERROR) as unknown as NotesFinalResponse;
  }

  const notesService = new NotesService();

  try {
    return await notesService.saveNotes(persistableNotes);
  } catch (error) {
    return throwError(NOTES_UPDATING_NOTES_ERROR_MESSAGE, error) as unknown as NotesFinalResponse;
  }
};

export const useSaveNotes = () => {
  const queryClient = useQueryClient();

  const saveNotesMutation = useMutation<NotesFinalResponse, Error, SaveNotesParams>({
    mutationFn: saveNotes,
  });

  const { data, isError, isPending, isSuccess, mutate } = saveNotesMutation;

  if (isSuccess) {
    // Invalidate the Save Notes to refetch the data
    queryClient.invalidateQueries({ queryKey: [GET_NOTES_KEY] });
    // Clean the mutation internal state
    saveNotesMutation.reset();
  }

  return {
    data,
    isError,
    isLoading: isPending,
    isSuccess,
    mutate,
  };
};
